const GAP = 8;
const MIN_SPACE_BELOW = 180;
const MIN_LIST_HEIGHT = 48;
const MAX_WAIT_FRAMES = 40;

const TRIGGER_SELECTOR = '.ant-table-filter-icon';
const DROPDOWN_SELECTOR = '.ant-table-filter-dropdown';
const HIDDEN_CLASS = 'ant-dropdown-hidden';
const LIST_SELECTORS = ['.scrollable-checkboxes', '.ant-dropdown-menu', '.filter-checkboxes'];

let installed = false;
let trigger: HTMLElement | null = null;
let popup: HTMLElement | null = null;
let observer: MutationObserver | null = null;

function isVisible(wrapper: HTMLElement): boolean {
    return !wrapper.classList.contains(HIDDEN_CLASS) && wrapper.offsetHeight > 0;
}

function getVisiblePopups(): HTMLElement[] {
    const result: HTMLElement[] = [];

    document.querySelectorAll<HTMLElement>(DROPDOWN_SELECTOR).forEach((dropdown) => {
        const wrapper = dropdown.closest('.ant-dropdown') as HTMLElement | null;

        if (wrapper && isVisible(wrapper)) {
            result.push(wrapper);
        }
    });

    return result;
}

function isScrollable(element: HTMLElement): boolean {
    const overflowY = window.getComputedStyle(element).overflowY;

    return overflowY === 'auto' || overflowY === 'scroll';
}

function findScrollableList(wrapper: HTMLElement): HTMLElement | null {
    for (const selector of LIST_SELECTORS) {
        const element = wrapper.querySelector(selector) as HTMLElement | null;

        if (element) {
            return element;
        }
    }

    const scrollable = Array.from(wrapper.querySelectorAll<HTMLElement>('*')).filter(isScrollable);

    return scrollable.length ? scrollable[0] : wrapper.querySelector(DROPDOWN_SELECTOR) as HTMLElement | null;
}

function setStyle(
    element: HTMLElement,
    property: 'maxHeight' | 'overflowY' | 'boxSizing' | 'position' | 'top' | 'left',
    value: string,
): void {
    if (element.style[property] !== value) {
        element.style[property] = value;
    }
}

function applyLayout(): void {
    if (!popup || !trigger || !trigger.isConnected || !isVisible(popup)) {
        return;
    }

    const triggerRect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const spaceBelow = viewportHeight - triggerRect.bottom - GAP * 2;
    const spaceAbove = triggerRect.top - GAP * 2;
    const openDown = spaceBelow >= MIN_SPACE_BELOW || spaceBelow >= spaceAbove;
    const space = Math.max(0, Math.floor(openDown ? spaceBelow : spaceAbove));
    const list = findScrollableList(popup);

    setStyle(popup, 'position', 'fixed');

    if (list) {
        const chrome = Math.max(0, popup.offsetHeight - list.offsetHeight);
        const allowed = Math.max(MIN_LIST_HEIGHT, space - chrome);

        if (list.scrollHeight > allowed) {
            setStyle(list, 'boxSizing', 'border-box');
            setStyle(list, 'maxHeight', `${allowed}px`);
            setStyle(list, 'overflowY', 'auto');
        } else {
            setStyle(list, 'maxHeight', '');
        }
    }

    const popupHeight = popup.offsetHeight;
    const popupWidth = popup.offsetWidth;
    const top = openDown ? triggerRect.bottom + GAP : triggerRect.top - GAP - popupHeight;
    const left = Math.min(
        Math.max(GAP, triggerRect.right - popupWidth),
        Math.max(GAP, viewportWidth - popupWidth - GAP),
    );

    setStyle(popup, 'top', `${Math.round(top)}px`);
    setStyle(popup, 'left', `${Math.round(left)}px`);
}

function onViewportChange(): void {
    if (!popup || !isVisible(popup)) {
        stopWatching();

        return;
    }

    applyLayout();
}

function stopWatching(): void {
    if (observer) {
        observer.disconnect();
        observer = null;
    }

    window.removeEventListener('scroll', onViewportChange, true);
    window.removeEventListener('resize', onViewportChange);

    popup = null;
    trigger = null;
}

function startWatching(wrapper: HTMLElement): void {
    popup = wrapper;

    if (observer) {
        observer.disconnect();
    }

    observer = new MutationObserver(() => {
        if (!popup || !isVisible(popup)) {
            stopWatching();

            return;
        }

        applyLayout();
    });

    observer.observe(wrapper, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: true,
        subtree: true,
    });

    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);

    applyLayout();
}

function waitForPopup(before: HTMLElement[], framesLeft: number): void {
    const appeared = getVisiblePopups().filter((wrapper) => before.indexOf(wrapper) === -1);

    if (appeared.length) {
        startWatching(appeared[appeared.length - 1]);

        return;
    }

    if (framesLeft > 0) {
        window.requestAnimationFrame(() => waitForPopup(before, framesLeft - 1));
    }
}

function onPointerDown(event: Event): void {
    const target = event.target as HTMLElement | null;
    const icon = target && target.closest ? target.closest(TRIGGER_SELECTOR) as HTMLElement | null : null;

    if (!icon) {
        return;
    }

    const before = getVisiblePopups();

    stopWatching();
    trigger = icon;
    waitForPopup(before, MAX_WAIT_FRAMES);
}

export function installFilterDropdownLayout(): void {
    if (installed || typeof document === 'undefined') {
        return;
    }

    installed = true;
    document.addEventListener('mousedown', onPointerDown, true);
}

export default installFilterDropdownLayout;
